import { useTranslation } from 'react-i18next'
import { Dropdown } from '@carsys/form'
import { VehicleSource } from '@cbgms/api'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import { FieldArray, useFormikContext } from 'formik'
import { IFormValues } from './types'
import { CountryCode } from '@carsys/enums/country-code'
import { COUNTRYOPTIONS } from 'core/constants/countries'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { VehicleIdType } from '@cbgms/api/modules/enums/vehicle-id-type'
import { CountriesDropdownFormik } from '@carsys/form/src/CountriesDropdownFormik'
import { useFeature } from '@cbgms/base/feature-flags'

const vehicleSourceOptions = [
  { text: VehicleSource.Unconfigured, value: VehicleSource.Unconfigured },
  { text: VehicleSource.CARSYS_HBASE, value: VehicleSource.CARSYS_HBASE },
  { text: VehicleSource.CARSYS_EMDM, value: VehicleSource.CARSYS_EMDM },
  { text: VehicleSource.CARSYS_AAA, value: VehicleSource.CARSYS_AAA },
  { text: VehicleSource.CARSYS_RDW, value: VehicleSource.CARSYS_RDW },
  { text: VehicleSource.VPOM_EMDM, value: VehicleSource.VPOM_EMDM },
  { text: VehicleSource.VPOM_TecDocPlus, value: VehicleSource.VPOM_TecDocPlus }
]

const vehicleTargetOptions = [
  { text: VehicleIdType.Registration, value: VehicleIdType.Registration },
  { text: VehicleIdType.VIN, value: VehicleIdType.VIN }
]

const emptyVehicleSource = {
  Source: VehicleSource.Unconfigured,
  Target: VehicleIdType.Registration,
  Country: CountryCode.ZZ
}

const b2bVpomHbaseSetupFeatureFlag = 'b2b-vpom-hbase-setup'
const visRdwProviderFeatureFlag = 'vis-rdw-provider'

export const getVehicleSourceOptions = (vpomHbaseEnabled: boolean, visRdwEnabled: boolean) => {
  let options = vpomHbaseEnabled
    ? [...vehicleSourceOptions, { text: VehicleSource.VPOM_Hbase, value: VehicleSource.VPOM_Hbase }]
    : vehicleSourceOptions
  if (visRdwEnabled) {
    options = [...options, { text: VehicleSource.CARSYS_VIS_RDW, value: VehicleSource.CARSYS_VIS_RDW }]
  }
  return options
}

export const CatalogFieldsVehicleSource = () => {
  const { t } = useTranslation()
  const vpomHbaseEnabled = useFeature(b2bVpomHbaseSetupFeatureFlag)
  const visRdwEnabled = useFeature(visRdwProviderFeatureFlag)

  return (
    <>
      <Dropdown
        label={t('wholesaler-catalog.VehiclePrimaryVehicleSource-mmt-dropdown-label')}
        name='VehiclePrimaryVehicleSource'
        options={getVehicleSourceOptions(vpomHbaseEnabled, visRdwEnabled)}
      />
      <Box>
        <VehicleSourcesConfigArrayField />
      </Box>
    </>
  )
}

const VehicleSourcesConfigArrayField = () => {
  const name = 'VehicleSources'
  const { values } = useFormikContext<IFormValues>()
  const { t } = useTranslation()
  const vpomHbaseEnabled = useFeature(b2bVpomHbaseSetupFeatureFlag)
  const visRdwEnabled = useFeature(visRdwProviderFeatureFlag)

  return (
    <Box>
      <FieldArray name={name}>
        {({ remove, push }) => (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='h6'>{t('wholesaler-catalog.vehicle-sources-config')}</Typography>
              <IconButton data-testid='add-vehicle-source-config' onClick={() => push(emptyVehicleSource)} size='large'>
                <AddIcon />
              </IconButton>
            </Box>
            {(values[name] || []).map((_, index) => (
              <Box key={`${name}[${index}]`} sx={{ display: 'flex', mb: 1 }}>
                <Box sx={{ display: 'inline-flex', flex: '1 1 auto', gap: 1, '&>*': { flexGrow: 1, width: '100%' } }}>
                  <CountriesDropdownFormik
                    required
                    countryOptions={COUNTRYOPTIONS}
                    name={`${name}[${index}].Country`}
                    label={t('wholesale.select_country')}
                  />
                  <Dropdown
                    label={t('wholesaler-catalog.VehicleSources-target')}
                    name={`${name}[${index}].Target`}
                    options={vehicleTargetOptions}
                    fullWidth={false}
                    required
                  />
                  <Dropdown
                    label={t('wholesaler-catalog.VehicleSources-source')}
                    name={`${name}[${index}].Source`}
                    options={getVehicleSourceOptions(vpomHbaseEnabled, visRdwEnabled)}
                    fullWidth={false}
                    required
                  />
                </Box>
                <Box display='inline-flex'>
                  <IconButton sx={{ paddingTop: 0, paddingBottom: 0 }} onClick={() => remove(index)} size='large'>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </>
        )}
      </FieldArray>
    </Box>
  )
}
